import { inject, injectable, named, } from '../shared/inversify';
import { CommandService, type Command, type CommandHandler } from './command.interface';
import { ContributionProvider } from './contribution-provider';
import { Disposable } from './disposable';
import { Emitter } from './event';
import { Message } from './message';

export { CommandService, type Command, type CommandHandler } from './command.interface';

export const CommandContribution = Symbol.for('CommandContribution');

export interface CommandContribution {
    registerCommands(commands: CommandRegistry): void;
}

/**
 * Represents a command registry that manages the registration and execution of commands.
 */
@injectable()
export class CommandRegistry implements CommandService {
    /**
     * Creates an instance of CommandRegistry.
     * @param _message The message service used for displaying error messages.
     * @param _contributionProvider The contribution provider used for retrieving command contributions.
     */
    constructor(
        @inject(Message)
        private readonly _message: Message,
        @inject(ContributionProvider) @named(CommandContribution)
        private readonly _contributionProvider: ContributionProvider<any/*CommandContribution*/>,
    ) {
        //
    }

    /**
     * The dictionary of registered commands.
     */
    protected readonly _commands: { [id: string]: Command } = {};

    /**
     * The dictionary of command handlers.
     */
    protected readonly _handlers: { [id: string]: CommandHandler[] } = {}

    protected readonly onCommandsChangedEmitter = new Emitter<void>();
    readonly onCommandsChanged = this.onCommandsChangedEmitter.event;

    /**
     * Initializes the command registry by registering commands from contributions.
     */
    onStart(): void {
        this._contributionProvider.getContributions().forEach(contribution => {
            contribution.registerCommands(this);
        });
    }

    /**
     * Registers a command with an optional command handler.
     * @param command The command to register.
     * @param handler The command handler to associate with the command.
     * @returns A disposable object that can be used to unregister the command and handler.
     */
    registerCommand(command: Command, handler?: CommandHandler): Disposable {
        const commandId = command.id;
        if (this._commands[commandId]) {
            console.warn(`Command ${commandId} already registered.`);
            return Disposable.NULL;
        }
        let disposables: Disposable[] = [];
        disposables.push(this.doRegisterCommand(command));
        if (handler) {
            disposables.push(this.registerHandler(commandId, handler));
        }
        return {
            dispose: () => {
                disposables.forEach(d => d.dispose());
            },
        };
    }

    /**
     * Registers a command in the command registry.
     * @param command The command to register.
     * @returns A disposable object that can be used to unregister the command.
     */
    private doRegisterCommand(command: Command): Disposable {
        this._commands[command.id] = command;
        return {
            dispose: () => {
                delete this._commands[command.id];
            }
        };
    }

    /**
     * Registers a command handler for a specific command.
     * @param commandId The ID of the command.
     * @param handler The command handler to register.
     * @returns A disposable object that can be used to unregister the command handler.
     */
    private registerHandler(commandId: string, handler: CommandHandler): Disposable {
        let handlers = this._handlers[commandId];
        if (!handlers) {
            this._handlers[commandId] = handlers = [];
        }
        handlers.unshift(handler);
        this.fireDidChange()
        return {
            dispose: () => {
                const idx = handlers.indexOf(handler);
                if (idx >= 0) {
                    handlers.splice(idx, 1);
                    this.fireDidChange()
                }
            }
        };
    }

    protected fireDidChange = () => {
        setTimeout(() => {
            this.doFireDidChange();
        }, 0);
    };

    protected doFireDidChange(): void {
        this.onCommandsChangedEmitter.fire();
    }

    /**
     * Retrieves a command by its ID.
     * @param id The ID of the command.
     * @returns The command with the specified ID, or undefined if not found.
     */
    getCommand(id: string): Command | undefined {
        return this._commands[id];
    }

    /**
     * Executes a command with the specified ID and arguments.
     * @param commandId The ID of the command to execute.
     * @param args The arguments to pass to the command handler.
     * @returns A promise that resolves to the result of the command execution.
     * @throws An error if the command cannot be executed due to the absence of active handlers.
     */
    async executeCommand<T>(commandId: string, ...args: any[]): Promise<T> {
        const handler = this.getActiveHandler(commandId, ...args);
        if (handler) {
            const result = await handler.execute(...args);
            return result;
        }
        this._message.error({
            message: `The command '${commandId}' cannot be executed. There are no active handlers available for the command.`
        });
        throw Object.assign(new Error(`The command '${commandId}' cannot be executed. There are no active handlers available for the command.`), { code: 'NO_ACTIVE_HANDLER' });
    }

    /**
     * Retrieves the active command handler for the specified command ID and arguments.
     * @param commandId The ID of the command.
     * @param args The arguments to pass to the command handler.
     * @returns The active command handler, or undefined if not found.
     */
    getActiveHandler(commandId: string, ...args: any[]): CommandHandler | undefined {
        const handlers = this._handlers[commandId];
        if (!handlers) {
            return undefined;
        }
        for (const handler of handlers) {
            if (handler.isEnabled && !handler.isEnabled(...args)) {
                continue;
            }
            return handler;
        }
        return undefined;
    }

    getAllCommands(): Readonly<Command & { handlers: CommandHandler[] }>[] {
        return Object.values(this._commands).map(command => {
            return {
                ...command,
                handlers: this._handlers[command.id] || []
            };
        });
    }
}

